import type { DefineComponent } from 'vue'
import { h, resolveDynamicComponent } from 'vue'
import type {
  CodeBlockAttributes,
  HeadingAttributes,
  ImageAttributes,
  LinkAttributes,
  NodeAttributes,
  OrderedListAttributes,
  StyledAttributes,
  TextNode,
} from '@marvr/storyblok-rich-text-types'
import {
  LinkTypes,
  NodeTypes,
} from '@marvr/storyblok-rich-text-types'
import { includes, kebabCase } from 'lodash-es'

export type RenderedNode = ReturnType<typeof h>
export type Component = DefineComponent<NonNullable<unknown>, NonNullable<unknown>, any>

export type BlockResolverFunction = () => RenderedNode | RenderedNode[]
export type BlockResolverFunctionWithOptions<O extends Record<string, any>> = (options: O) => RenderedNode | RenderedNode[]

export type BlockResolver = Component | BlockResolverFunction

export type BlockResolverWithChildren = Component | BlockResolverFunctionWithOptions<{ children: RenderedNode[] }>

export type BlockResolverWithAttributes<A extends NodeAttributes> = Component | BlockResolverFunctionWithOptions<{ attrs: A }>

export type BlockResolverWithChildrenAndAttributes<A extends NodeAttributes> =
  | Component
  | BlockResolverFunctionWithOptions<{ children: RenderedNode[]; attrs: A }>

export type MarkResolverFunction = (options: { text: TextNode['text'] }) => RenderedNode

export type MarkResolverFunctionWithAttributes<A extends NodeAttributes> = (options: { text: TextNode['text']; attrs: A }) => RenderedNode

export type MarkResolver = Component | MarkResolverFunction
export type MarkResolverWithAttributes<A extends NodeAttributes> = Component | MarkResolverFunctionWithAttributes<A>

export type ComponentFields = Record<string, any>

export interface ComponentOptions {
  id: string
  _uid: string
  component: string
  fields: Record<string, any>
}

export type ComponentResolverFunction = (options: ComponentOptions) => RenderedNode

export type ComponentResolvers = Record<string, ComponentResolverFunction>

export interface Resolvers {
  // Blocks
  [NodeTypes.DOCUMENT]: BlockResolverWithChildren
  [NodeTypes.HEADING]: BlockResolverWithChildrenAndAttributes<HeadingAttributes>
  [NodeTypes.PARAGRAPH]: BlockResolverWithChildren
  [NodeTypes.QUOTE]: BlockResolverWithChildren
  [NodeTypes.OL_LIST]: BlockResolverWithChildrenAndAttributes<OrderedListAttributes>
  [NodeTypes.UL_LIST]: BlockResolverWithChildren
  [NodeTypes.LIST_ITEM]: BlockResolverWithChildren
  [NodeTypes.CODE_BLOCK]: BlockResolverWithChildrenAndAttributes<CodeBlockAttributes>
  [NodeTypes.HR]: BlockResolver
  [NodeTypes.BR]: BlockResolver
  [NodeTypes.IMAGE]: BlockResolverWithAttributes<ImageAttributes>
  // Marks
  [NodeTypes.BOLD]: MarkResolver
  [NodeTypes.STRONG]: MarkResolver
  [NodeTypes.STRIKE]: MarkResolver
  [NodeTypes.UNDERLINE]: MarkResolver
  [NodeTypes.ITALIC]: MarkResolver
  [NodeTypes.CODE]: MarkResolver
  [NodeTypes.LINK]: MarkResolverWithAttributes<LinkAttributes>
  [NodeTypes.STYLED]: MarkResolverWithAttributes<StyledAttributes>
  // Fallback component
  [NodeTypes.COMPONENT]: () => RenderedNode
}

export const defaultResolvers: Resolvers = {
  // Blocks
  [NodeTypes.DOCUMENT]: ({ children }) => children,
  [NodeTypes.HEADING]: ({ children, attrs }) => {
    if (children[0]?.type === 'span')
      return h(`h${attrs.level}`, { class: ['mb-2', children[0].props?.class], id: children[0]?.children && kebabCase(children[0].children) }, children[0].children)

    return h(`h${attrs.level}`, { class: ['mb-2.5 mt-10'], id: children[0]?.children && kebabCase(children[0].children) }, children)
  },
  [NodeTypes.PARAGRAPH]: ({ children }) => {
    if (includes(children?.at(0)?.props?.class, 'self-center'))
      h('p', { class: 'self-center' }, children)

    return h('p', children)
  },
  [NodeTypes.QUOTE]: ({ children }) => h('blockquote', children),
  [NodeTypes.OL_LIST]: ({ children }) => h('ol', { class: 'list-decimal pl-5' }, children),
  [NodeTypes.UL_LIST]: ({ children }) => {
    if (includes(children?.at(0)?.children?.at(0)?.children?.at(0)?.props?.class, 'self-center'))
      return h('div', { class: 'self-center' }, [h('ul', { class: 'list-disc pl-6' }, children)])

    return h('ul', { class: 'list-disc pl-6' }, children)
  },
  [NodeTypes.LIST_ITEM]: ({ children }) => h('li', children),
  [NodeTypes.CODE_BLOCK]: ({ children, attrs }) => h('pre', attrs, children),
  [NodeTypes.HR]: () => h('hr', { class: 'my-8 ' }),
  [NodeTypes.BR]: () => h('br'),
  [NodeTypes.IMAGE]: ({ attrs }) => h('img', {
    ...attrs,
    loading: 'lazy',
    src: attrs.src.replace('a.storyblok.com', `img2.storyblok.com/${['800x0'].filter(Boolean).join('/')}`),
  }),
  // Marks
  [NodeTypes.BOLD]: ({ text }) => h('b', text),
  [NodeTypes.STRONG]: ({ text }) => h('strong', text),
  [NodeTypes.STRIKE]: ({ text }) => h('s', text),
  [NodeTypes.UNDERLINE]: ({ text }) => h('u', text),
  [NodeTypes.ITALIC]: ({ text }) => h('i', text),
  [NodeTypes.CODE]: ({ text }) => h('code', text),
  [NodeTypes.LINK]: ({ text, attrs }) => {
    let href = ''
    const url = attrs.href.split('@')
    const params = url[1] ? url[1].split('=') : []
    switch (attrs.linktype) {
      case LinkTypes.ASSET:
      case LinkTypes.URL:
        href = url[0]
        break
      case LinkTypes.EMAIL:
        href = `mailto:${attrs.href}`
        break
      case LinkTypes.STORY: {
        const RouterLink = getRouterLinkComponent()
        if (!RouterLink)
          return h('a', { href, target: attrs.target }, text)

        return h(RouterLink, {
          to: useTrailingSlash(attrs.href.replace('//', '/')) + (attrs.anchor ? `#${attrs.anchor}` : ''),
          target: attrs.target,
          class: 'text-secondary-active hover:underline hover:text-hover',
        }, { default: () => text })
      }
    }
    return h('a', { href, target: attrs.target, [params[0]]: params[1], class: 'text-secondary-active hover:underline hover:text-hover' }, text)
  },
  [NodeTypes.STYLED]: ({ text, attrs }) => h('span', attrs, text),
  // Component fallback
  [NodeTypes.COMPONENT]: () => {
    return h('div', 'Failed to load component')
  },
}

export function getRouterLinkComponent(): false | Component {
  const component = resolveDynamicComponent('RouterLink')
  return typeof component === 'string' ? false : (component as Component)
}
